import {
  useEffect,
  FC,
  useState,
  Dispatch,
  SetStateAction,
  UIEvent,
  useMemo,
} from 'react'
import { useParams } from 'react-router-dom'
import { UseQueryResult } from '@tanstack/react-query'
import moment from 'moment'
import { toast } from 'react-toastify'
import { INITIAL_ZOOM } from 'src/charts/LocationChartSection/constants/zoom'
import { MentionsByMonth } from 'src/charts/MentionsByMonth'
import { TopSourcesSection } from 'src/charts/TopSourcesSection'
import { SentimentAnalysesSection } from 'src/charts/SentimentAnalysesSection'
import { CampaignCommentsSection } from 'src/charts/CampaignCommentsSection'
import { MentionDetailsModal } from 'src/charts/MentionDetailsModal'
import {
  CAMPAIGNS_ROUTE,
  UPDATE_CAMPAIGN_BASE_ROUTE,
} from 'src/constants/routes'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { useGetTopicsTableQuery } from 'src/reactQuery/useGetSocialTopicsQuery'
import { TopicsTableSection } from 'src/charts/TopicsSubtopics/TopicsTableSection'
import { DailyTrendsSection } from 'src/charts/DailyTrendsSection'
import { useGetLocationsQuery } from 'src/reactQuery/useGetLocationsQuery'
import { adjustZoomToAPI } from 'src/reactQuery/useGetLocationsQuery/utils/adjustZoomToAPI'
import { useGetAllSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetAllSentimentAnalysisThreadsQuery'
import { useGetNegativeSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetNegativeSentimentAnalysisThreadsQuery'
import { useGetNeutralSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetNeutralSentimentAnalysisThreadsQuery'
import { useGetPositiveSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetPositiveSentimentAnalysisThreadsQuery'
import {
  AnalyticsFetchThreadsResponse,
  AnalyticsFetchThreadsSentimentType,
  FetchThreadsCSVIsLostType,
} from 'src/services/__generated__/api'
import { useGetDailyTrendsByCampaignQuery } from 'src/reactQuery/useGetDailyTrendsByCampaignQuery'
import { useGetSentimentChartByCampaignQuery } from 'src/reactQuery/useGetSentimentChartByCampaignQuery'
import { useGetSentimentAnalysisByCampaignQuery } from 'src/reactQuery/useGetSentimentAnalysisByCampaignQuery'
import { useGetTopSourcesByCampaignQuery } from 'src/reactQuery/useGetTopSourcesByCampaignQuery'
import { useGetCalendarByCampaignQuery } from 'src/reactQuery/useGetCalendarByCampaignQuery'
import { OnFilterSelectParams } from 'src/charts/SentimentChartSection/interfaces/onFilterSelect.interface'
import { getEndOfToday } from 'src/utils/date'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { cn } from 'src/utils/cn'
import { CampaignDataComparison } from 'src/pages/ThemeDetail/CampaignDataComparison'
import {
  CampaignDetailHeader,
  CampaignDetailView,
} from 'src/pages/ThemeDetail/CampaignDetailHeader'
import { UseGetSentimentAnalysisByCampaignQueryParams } from 'src/reactQuery/useGetSentimentAnalysisByCampaignQuery/interfaces/useGetSentimentAnalysisByCampaignQuery.interface'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { useGetKeywordsAnalysis } from 'src/reactQuery/useGetKeywordsAnalysis'
import { KeywordAnalyses } from 'src/charts/KeywordAnalyses'
import { UseGetKeywordsAnalysisParams } from 'src/reactQuery/useGetKeywordsAnalysis/interfaces/useGetKeywordsAnalysis.interface'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { useGetCampaignByIdQuery } from 'src/reactQuery/useGetCampaignById'
import { useGetCustomCalendarRangeQuery } from 'src/reactQuery/useGetCustomCalendarRangeQuery'
import en from 'src/constants/en'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { useDownloadFileByUrl } from 'src/hooks/useDownloadFIleByUrl'
import { SmartInsightsAIRecommendations } from 'src/charts/SmartInsightsAIRecommendations'
import { ScopedSummarySection } from 'src/charts/SummarySection'
import { useGetCampaignsOverviewQuery } from 'src/reactQuery/useGetCampaignsOverviewQuery'
import { CampaignDetailHeroSection } from './CampaignDetailHeroSection'
import { ThreadState } from './interfaces/threadState.interface'
import styles from './themeDetail.module.scss'
import { QueryParams } from '../Campaigns/interfaces/queryParams.interface'

const DEFAULT_THREAD_LIMIT = 20
const THREAD_LIMIT_INCREMENT = 10

const INIT_THREAD_STATE: ThreadState = {
  data: null,
  isScrolledDown: false,
  isScrolledUp: false,
  limit: DEFAULT_THREAD_LIMIT,
}

const ThemeDetail: FC = () => {
  const sessionId = getSessionIdFromLocalStorage()

  // Which of the two top-level views the page shows. Only the toggle reads it
  // for now; the comparison view it will switch to is not built yet.
  const [view, setView] = useState<CampaignDetailView>('general')

  const [locationZoom] = useState(INITIAL_ZOOM)

  const [sentimentChartFilters] = useState<OnFilterSelectParams | null>(null)

  const [keywordAnalysesFilters, setKeywordAnalysesFilters] =
    useState<UseGetKeywordsAnalysisParams>()

  const [sentimentAnalysesFilters, setSentimentAnalysesFilters] =
    useState<
      Pick<
        UseGetSentimentAnalysisByCampaignQueryParams,
        | 'isLost'
        | 'selectedKeywords'
        | 'selectedLanguages'
        | 'selectedMentionFormats'
        | 'selectedMentionTypes'
        | 'selectedSources'
      >
    >()

  const [searchText, setSearchText] = useState('')
  // The mention whose Post Details modal is open (View Details on a row).
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [debouncedSearchText, setDebouncedSearchText] = useState('')

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchText])

  const backURL = useCustomLink(CAMPAIGNS_ROUTE)

  const { themeId } = useParams<{
    themeId: string
  }>()

  // Carries the active date filter across to the edit screen, the same way the
  // back link does — a bare path would reset the period on return.
  const editURL = useCustomLink(`${UPDATE_CAMPAIGN_BASE_ROUTE}/${themeId}`)

  const { queryParams } = useQueryParams<QueryParams>()

  const dates = getDateRangeByQueryParams(queryParams)

  const startDate = dates?.start
  const endDate = dates?.end

  const { data: topicsTable, isLoading: topicsTableLoading } =
    useGetTopicsTableQuery(themeId, {
      fromDate: startDate,
      toDate: endDate,
    })

  const { data: campaignData } = useGetCampaignByIdQuery({
    id: themeId,
  })

  const { data: campaignsOverviewData, isFetching: isOverviewFetching } =
    useGetCampaignsOverviewQuery({
      campaignIds: themeId,
      end: endDate,
      start: startDate,
    })

  const themeName = campaignData?.campaign?.name
  const createdAt = campaignData?.campaign?.createdAt
  // An account with no Social Listening campaigns still reaches this page, and
  // it was offering to download a mentions report and edit a campaign that
  // does not exist. Both actions belong to a campaign, so they appear only
  // once there is one.
  const hasCampaign = !!campaignData?.campaign

  useGetCustomCalendarRangeQuery()

  const { isLoading: isCsvLoading, handleFileDownload } = useDownloadFileByUrl()

  const { refetch: refetchLocations } = useGetLocationsQuery({
    campaign: themeId,
    fromDate: startDate || undefined,
    toDate: endDate || undefined,
    zoom: adjustZoomToAPI(locationZoom),
  })

  const {
    data: dailyTrendsData,
    refetch: refetchDailyTrends,
    isFetching: isDailyTrendsFetching,
  } = useGetDailyTrendsByCampaignQuery({
    campaign: themeId,
    end: endDate,
    start: startDate,
  })

  const {
    data: topSourcesData,
    refetch: refetchTopSources,
    isFetching: isTopSourcesFetching,
  } = useGetTopSourcesByCampaignQuery({
    campaign: themeId,
    end: endDate,
    start: startDate,
  })

  const sourcesList = useMemo(
    () =>
      topSourcesData
        ?.map((s) => s.name)
        .filter((n): n is string => Boolean(n)) ?? [],
    [topSourcesData]
  )

  const { refetch: refetchSentimentChart } =
    useGetSentimentChartByCampaignQuery({
      campaign: themeId,
      end: endDate,
      keywords: sentimentChartFilters?.keywords,
      sources: sentimentChartFilters?.sources,
      start: startDate,
    })

  const {
    data: sentimentAnalysisData,
    refetch: refetchSentimentAnalysis,
    isFetching: isSentimentAnalysisFetching,
  } = useGetSentimentAnalysisByCampaignQuery({
    campaign: themeId,
    end: endDate,
    isLost: sentimentAnalysesFilters?.isLost,
    selectedKeywords: sentimentAnalysesFilters?.selectedKeywords,
    selectedLanguages: sentimentAnalysesFilters?.selectedLanguages,
    selectedMentionFormats: sentimentAnalysesFilters?.selectedMentionFormats,
    selectedMentionTypes: sentimentAnalysesFilters?.selectedMentionTypes,
    selectedSources: sentimentAnalysesFilters?.selectedSources,
    start: startDate,
  })

  const {
    data: calendarData,
    refetch: refetchCalendar,
    isFetching: isCalendarFetching,
  } = useGetCalendarByCampaignQuery({
    campaign: themeId,
    fromDate: startDate,
    toDate: endDate,
  })

  const { data: keywordsAnalysisData, isFetching: isKeywordsAnalysisFetching } =
    useGetKeywordsAnalysis({
      campaign: themeId,
      filterChoice: keywordAnalysesFilters?.filterChoice,
      filterCount: keywordAnalysesFilters?.filterCount,
      fromDate: startDate,
      selectedKeywords: keywordAnalysesFilters?.selectedKeywords,
      sentimentType: keywordAnalysesFilters?.sentimentType,
      toDate: endDate,
    })

  const [allSentimentAnalysisThreads, setAllSentimentAnalysisThreads] =
    useState<ThreadState>({ ...INIT_THREAD_STATE })

  const [
    positiveSentimentAnalysisThreads,
    setPositiveSentimentAnalysisThreads,
  ] = useState<ThreadState>({ ...INIT_THREAD_STATE })

  const [
    negativeSentimentAnalysisThreads,
    setNegativeSentimentAnalysisThreads,
  ] = useState<ThreadState>({ ...INIT_THREAD_STATE })

  const [neutralSentimentAnalysisThreads, setNeutralSentimentAnalysisThreads] =
    useState<ThreadState>({ ...INIT_THREAD_STATE })

  useEffect(() => {
    const resetPagination = (prev: ThreadState) => ({
      ...prev,
      isScrolledDown: false,
      isScrolledUp: false,
      limit: DEFAULT_THREAD_LIMIT,
    })
    setAllSentimentAnalysisThreads(resetPagination)
    setPositiveSentimentAnalysisThreads(resetPagination)
    setNegativeSentimentAnalysisThreads(resetPagination)
    setNeutralSentimentAnalysisThreads(resetPagination)
  }, [debouncedSearchText])

  const getThreadsStateFromSentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ): {
    setState: Dispatch<SetStateAction<ThreadState>>
    state: ThreadState
  } => {
    const sentimentsToThreadsStateMap = {
      ALL: {
        setState: setAllSentimentAnalysisThreads,
        state: allSentimentAnalysisThreads,
      },
      NEG: {
        setState: setNegativeSentimentAnalysisThreads,
        state: negativeSentimentAnalysisThreads,
      },
      NEU: {
        setState: setNeutralSentimentAnalysisThreads,
        state: neutralSentimentAnalysisThreads,
      },
      POS: {
        setState: setPositiveSentimentAnalysisThreads,
        state: positiveSentimentAnalysisThreads,
      },
    }

    return sentiment
      ? sentimentsToThreadsStateMap[sentiment]
      : sentimentsToThreadsStateMap.ALL
  }

  const allSentimentAnalysisThreadsQuery =
    useGetAllSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate || undefined,
      isLost: sentimentAnalysesFilters?.isLost,
      lastDate: getEndOfToday(),
      limit: allSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      selectedKeywords: sentimentAnalysesFilters?.selectedKeywords,
      selectedLanguages: sentimentAnalysesFilters?.selectedLanguages,
      selectedMentionFormats: sentimentAnalysesFilters?.selectedMentionFormats,
      selectedMentionTypes: sentimentAnalysesFilters?.selectedMentionTypes,
      selectedSources: sentimentAnalysesFilters?.selectedSources,
      toDate: endDate || undefined,
    })

  const positiveSentimentAnalysisThreadsQuery =
    useGetPositiveSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate || undefined,
      isLost: sentimentAnalysesFilters?.isLost,
      lastDate: getEndOfToday(),
      limit: positiveSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      selectedKeywords: sentimentAnalysesFilters?.selectedKeywords,
      selectedLanguages: sentimentAnalysesFilters?.selectedLanguages,
      selectedMentionFormats: sentimentAnalysesFilters?.selectedMentionFormats,
      selectedMentionTypes: sentimentAnalysesFilters?.selectedMentionTypes,
      selectedSources: sentimentAnalysesFilters?.selectedSources,
      toDate: endDate || undefined,
    })

  const negativeSentimentAnalysisThreadsQuery =
    useGetNegativeSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate || undefined,
      isLost: sentimentAnalysesFilters?.isLost,
      lastDate: getEndOfToday(),
      limit: negativeSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      selectedKeywords: sentimentAnalysesFilters?.selectedKeywords,
      selectedLanguages: sentimentAnalysesFilters?.selectedLanguages,
      selectedMentionFormats: sentimentAnalysesFilters?.selectedMentionFormats,
      selectedMentionTypes: sentimentAnalysesFilters?.selectedMentionTypes,
      selectedSources: sentimentAnalysesFilters?.selectedSources,
      toDate: endDate || undefined,
    })

  const neutralSentimentAnalysisThreadsQuery =
    useGetNeutralSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate || undefined,
      lastDate: getEndOfToday(),
      limit: neutralSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      selectedKeywords: sentimentAnalysesFilters?.selectedKeywords,
      selectedLanguages: sentimentAnalysesFilters?.selectedLanguages,
      selectedMentionFormats: sentimentAnalysesFilters?.selectedMentionFormats,
      selectedMentionTypes: sentimentAnalysesFilters?.selectedMentionTypes,
      selectedSources: sentimentAnalysesFilters?.selectedSources,
      toDate: endDate || undefined,
    })

  const getThreadsQueryFromSentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ): UseQueryResult<AnalyticsFetchThreadsResponse | null, unknown> => {
    const sentimentsToThreadsQueryMap = {
      ALL: allSentimentAnalysisThreadsQuery,
      NEG: negativeSentimentAnalysisThreadsQuery,
      NEU: neutralSentimentAnalysisThreadsQuery,
      POS: positiveSentimentAnalysisThreadsQuery,
    }

    return sentiment
      ? sentimentsToThreadsQueryMap[sentiment]
      : sentimentsToThreadsQueryMap.ALL
  }

  const setThreadsDataBySentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => {
    const { setState } = getThreadsStateFromSentiment(sentiment)

    const { data } = getThreadsQueryFromSentiment(sentiment)

    if (data) {
      setState((prev) => ({
        ...prev,
        data: data.data || null,
        isScrolledDown: false,
      }))
    }
  }

  const refetchThreadsBySentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => {
    const { isScrolledUp, isScrolledDown } =
      getThreadsStateFromSentiment(sentiment).state
    const { refetch, isLoading } = getThreadsQueryFromSentiment(sentiment)

    const shouldRefetch = isScrolledUp || isScrolledDown

    if (shouldRefetch && !isLoading) {
      refetch()
    }
  }

  const handleThreadsScroll = (
    event: UIEvent,
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => {
    if (!event.currentTarget) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget

    const {
      state: { isScrolledUp },
      setState,
    } = getThreadsStateFromSentiment(sentiment)

    const isScrolledCloseToTop = scrollTop < 40
    const isScrolledCloseToBottom = scrollTop + clientHeight > scrollHeight - 40

    if (isScrolledCloseToTop !== isScrolledUp) {
      setState((prev) => ({ ...prev, isScrolledUp: isScrolledCloseToTop }))
    }

    if (isScrolledCloseToBottom) {
      setState((prev) => {
        if (prev.isScrolledDown) return prev
        return {
          ...prev,
          isScrolledDown: true,
          limit: prev.limit + THREAD_LIMIT_INCREMENT,
        }
      })
    }
  }

  const handleDownloadCallback = async () => {
    const response =
      await AnalyticsApi.v1Private.analyticsFetchCampaignExcelList({
        campaign: themeId,
        fromDate: startDate,
        isLost: sentimentAnalysesFilters?.isLost as
          | FetchThreadsCSVIsLostType
          | undefined,
        languages: sentimentAnalysesFilters?.selectedLanguages,
        mentionFormat: sentimentAnalysesFilters?.selectedMentionFormats,
        selectedKeywords: sentimentAnalysesFilters?.selectedKeywords,
        selectedSources: sentimentAnalysesFilters?.selectedSources,
        toDate: endDate,
      })

    const url = window.URL.createObjectURL(await response.blob())
    const contentDisposition = response.headers.get('content-disposition')
    const fileNameMatch = contentDisposition?.match(/filename="(.+)"/)
    const fileName = fileNameMatch?.[1]

    if (!fileName) {
      console.error('No filename found in Content-Disposition header')
      throw new Error('No filename provided from server')
    }

    return {
      fileName,
      url,
    }
  }

  const handleCSVDownload = async () => {
    try {
      await handleFileDownload(handleDownloadCallback)
    } catch (error) {
      toast.error(en.ERROR_CSV)
    }
  }

  useEffect(() => {
    setThreadsDataBySentiment()
  }, [allSentimentAnalysisThreadsQuery.data])

  useEffect(() => {
    setThreadsDataBySentiment(AnalyticsFetchThreadsSentimentType.POS)
  }, [positiveSentimentAnalysisThreadsQuery.data])

  useEffect(() => {
    setThreadsDataBySentiment(AnalyticsFetchThreadsSentimentType.NEG)
  }, [negativeSentimentAnalysisThreadsQuery.data])

  useEffect(() => {
    setThreadsDataBySentiment(AnalyticsFetchThreadsSentimentType.NEU)
  }, [neutralSentimentAnalysisThreadsQuery.data])

  useEffect(() => {
    refetchThreadsBySentiment()
  }, [
    allSentimentAnalysisThreads.isScrolledUp,
    allSentimentAnalysisThreads.isScrolledDown,
  ])

  useEffect(() => {
    refetchThreadsBySentiment(AnalyticsFetchThreadsSentimentType.POS)
  }, [
    positiveSentimentAnalysisThreads.isScrolledUp,
    positiveSentimentAnalysisThreads.isScrolledDown,
  ])

  useEffect(() => {
    refetchThreadsBySentiment(AnalyticsFetchThreadsSentimentType.NEG)
  }, [
    negativeSentimentAnalysisThreads.isScrolledUp,
    negativeSentimentAnalysisThreads.isScrolledDown,
  ])

  useEffect(() => {
    refetchThreadsBySentiment(AnalyticsFetchThreadsSentimentType.NEU)
  }, [
    neutralSentimentAnalysisThreads.isScrolledUp,
    neutralSentimentAnalysisThreads.isScrolledDown,
  ])

  useEffect(() => {
    if (themeId) {
      refetchCalendar()
    }

    if (sessionId && startDate && endDate) {
      refetchDailyTrends()

      refetchTopSources()

      refetchSentimentChart()

      refetchSentimentAnalysis()

      refetchLocations()

      // Reset pagination on filter change but keep previous data visible
      // until the refetch resolves, so the list shows a loading state
      // instead of flashing an empty state.
      const resetPagination = (prev: ThreadState) => ({
        ...prev,
        isScrolledDown: false,
        isScrolledUp: false,
        limit: DEFAULT_THREAD_LIMIT,
      })
      setAllSentimentAnalysisThreads(resetPagination)
      setPositiveSentimentAnalysisThreads(resetPagination)
      setNegativeSentimentAnalysisThreads(resetPagination)
      setNeutralSentimentAnalysisThreads(resetPagination)

      allSentimentAnalysisThreadsQuery.refetch()
      positiveSentimentAnalysisThreadsQuery.refetch()
      negativeSentimentAnalysisThreadsQuery.refetch()
      neutralSentimentAnalysisThreadsQuery.refetch()
    }
  }, [sessionId, themeId, startDate, endDate, sentimentAnalysesFilters])

  useEffect(() => {
    if (startDate && endDate) {
      refetchSentimentChart()
    }
  }, [startDate, endDate])

  useEffect(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }, [])

  // Data Comparison replaces the whole body — it carries its own copy of the
  // header (minus the actions) inside its card. Returned early so the general
  // view's widget tree stays flat rather than nested in a branch.
  if (view === 'comparison') {
    return (
      <div className={cn(styles.themeDetail, styles.themeDetailComparison)}>
        <CampaignDataComparison
          backUrl={backURL}
          campaignId={themeId}
          campaignName={themeName}
          date={createdAt ? moment(createdAt).format('DD/MM/YYYY') : undefined}
          onViewChange={setView}
          view={view}
        />
      </div>
    )
  }

  return (
    <div className={styles.themeDetail}>
      <CampaignDetailHeader
        backUrl={backURL}
        campaignName={themeName}
        date={createdAt ? moment(createdAt).format('DD/MM/YYYY') : undefined}
        editUrl={editURL}
        isDownloading={isCsvLoading}
        onDownload={handleCSVDownload}
        onViewChange={setView}
        parent="Campaigns"
        showActions={hasCampaign}
        view={view}
      />

      {/* Same window as every other widget on this page: without it the
          insights were unfiltered AND their "% of discussion" divided by the
          campaign's LIFETIME mentions, so 2 of the window's 47 read as 1%. */}
      <SmartInsightsAIRecommendations
        campaign={themeId}
        campaignCategories={campaignData?.campaign?.categories}
        fromDate={startDate || undefined}
        homePageVariant
        toDate={endDate || undefined}
      />

      <CampaignDetailHeroSection
        dailyTrends={dailyTrendsData}
        isLoading={isOverviewFetching && !campaignsOverviewData}
        overview={campaignsOverviewData?.data}
        sourcesList={sourcesList}
        topSourcesData={topSourcesData ?? []}
      />

      <TopicsTableSection
        campaignId={themeId}
        data={topicsTable}
        isLoading={topicsTableLoading}
        range={{ fromDate: startDate, toDate: endDate }}
      />

      <DailyTrendsSection
        chartData={dailyTrendsData || undefined}
        isLoading={isDailyTrendsFetching}
        showTotalMentions
      />

      {/* <SentimentChartSection
        chartData={sentimentChartData || undefined}
        isLoading={isLoadingSentimentChart}
        isNextYearDisabled={
          !!selectedSentimentChartYear &&
          selectedSentimentChartYear >= sentimentChartRangeEndYear
        }
        isPrevYearDisabled={
          !!selectedSentimentChartYear &&
          selectedSentimentChartYear <= sentimentChartRangeStartYear
        }
        onFilterSelect={(filters) => setFilters(filters)}
        onNextYearClick={() => {
          setSelectedSentimentChartYear((prev) => (prev || 0) + 1)
        }}
        onPrevYearClick={() => {
          setSelectedSentimentChartYear((prev) => (prev || 0) - 1)
        }}
        selectedYear={selectedSentimentChartYear || undefined}
      /> */}

      <SentimentAnalysesSection
        allThreadsData={allSentimentAnalysisThreads.data || undefined}
        applyFilters={(params) => {
          setSentimentAnalysesFilters((prev) => {
            return {
              ...prev,
              isLost: params.isLost,
              selectedKeywords:
                params.selectedKeywords.length > 0
                  ? params.selectedKeywords.join(',')
                  : undefined,
              selectedLanguages: params.selectedLanguages.join(','),
              selectedMentionFormats: params.selectedMentionFormats.join(','),
              selectedMentionTypes:
                params.selectedMentionTypes.length > 0
                  ? params.selectedMentionTypes.join(',')
                  : undefined,
              selectedSources:
                params.selectedSources.length > 0
                  ? params.selectedSources.join(',')
                  : undefined,
            }
          })
        }}
        chartData={sentimentAnalysisData || undefined}
        handleThreadsScroll={handleThreadsScroll}
        hasFilters
        isLoading={
          isSentimentAnalysisFetching ||
          allSentimentAnalysisThreadsQuery.isFetching ||
          negativeSentimentAnalysisThreadsQuery.isFetching ||
          neutralSentimentAnalysisThreadsQuery.isFetching ||
          positiveSentimentAnalysisThreadsQuery.isFetching
        }
        negativeThreadsData={negativeSentimentAnalysisThreads.data || undefined}
        neutralThreadsData={neutralSentimentAnalysisThreads.data || undefined}
        onSearchTextChange={setSearchText}
        onViewThreadDetails={setSelectedThreadId}
        positiveThreadsData={positiveSentimentAnalysisThreads.data || undefined}
      />

      {themeId && (
        <CampaignCommentsSection
          campaignId={themeId}
          fromDate={startDate}
          onViewThreadDetails={setSelectedThreadId}
          toDate={endDate}
        />
      )}

      <MentionDetailsModal
        onClose={() => setSelectedThreadId(null)}
        threadId={selectedThreadId}
      />

      <TopSourcesSection
        chartData={topSourcesData || undefined}
        isLoading={isTopSourcesFetching}
      />

      <KeywordAnalyses
        applyFilters={(params) => {
          setKeywordAnalysesFilters({
            filterChoice: params.type,
            filterCount: params.amount,
            selectedKeywords: params.selectedKeywords?.join(','),
          })
        }}
        isLoading={isKeywordsAnalysisFetching}
        keywordAnalysesFilters={keywordAnalysesFilters}
        keywordsAnalysisData={keywordsAnalysisData || undefined}
      />

      <MentionsByMonth
        chartData={calendarData || undefined}
        isLoading={isCalendarFetching}
        period={queryParams.period}
      />

      {/* AI Summary — the context, themes and drivers behind the keyword
          mentions, at the very bottom of the page. It keeps its own period
          (picked in the widget), independent of the top-bar date filter. */}
      {themeId && (
        <ScopedSummarySection
          scope={{ id: themeId, kind: 'socialListening' }}
        />
      )}
    </div>
  )
}

export default ThemeDetail
